<script setup lang="ts">
import { usePagination } from "~/composables/paginations/usePagination";
import { useCreateTrainingStore } from "~/store/trainings/create-training.store";
import GlobalModal from "~/components/Common/Modals/GlobalModal.vue";
import CenterModal from "~/components/Common/Modals/CenterModal.vue";
import { useTrainings } from "~/composables/trainings/useTrainings";
import TrainingFooter from "./TrainingFooter.vue";
import TrainingNav from "./TrainingNav.vue";
import TrainingStepContent from "./steps/content/TrainingStepContent.vue";
import TrainingStepGeneral from "./steps/general/TrainingStepGeneral.vue";
import TrainingStepModalities from "./steps/modalities/TrainingStepModalities.vue";
import TrainingStepSessionItems from "./steps/session-items/TrainingStepSessionItems.vue";
import TrainingStepSessions from "./steps/sessions/TrainingStepSessions.vue";
import TrainingChoiceTypeModal from "./choice-type-modal/TrainingChoiceTypeModal.vue";
import TrainingProgramViewer from "~/components/Pages/company-catalog/training-modal/prefill/TrainingProgramViewer.vue";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import { useTrainingPrefillStore } from "~/store/copilot/training-prefill.store";
import { useTrainingPrefillWatchers } from "~/composables/copilot/useTrainingPrefillWatchers";
import { TrainingTypeEnum } from "~/utils/enums/training.enums";
import { hostsTrainingModal, queryWithoutTrainingId } from "~/utils/helpers/trainings/training-modal-host";
import { useCompaniesStore } from "~/store/companies/companies.store";
const emit = defineEmits(["reset"]);

const tab: Array<any> = [
  TrainingStepGeneral,
  TrainingStepContent,
  TrainingStepSessionItems,
  TrainingStepModalities,
  TrainingStepSessions,
];

const router = useRouter();
const trainings = useTrainings();
const createTrainingStore = useCreateTrainingStore();
const prefillStore = useTrainingPrefillStore();
const { training, readonly } = storeToRefs(createTrainingStore);
const paginate = usePagination();
const type = ref<TrainingTypeEnum | null>(null);

// Editing a pre-filled field drops its ✦ marker — one registration for the fields of every step.
useTrainingPrefillWatchers();

/** There is a program to show at all: the panel and the strip it folds into both hang off this. */
const hasProgram = computed(() => prefillStore.isActive && prefillStore.hasDocument);

function onClose() {
  // Clearing `?id=` and refreshing the list behind the form are the host page's own affair: over
  // another page that query is not the form's, and the list to refresh is not the catalog's.
  const hostsForm = hostsTrainingModal(router.currentRoute.value);
  if (hostsForm) router.push({ query: queryWithoutTrainingId(router.currentRoute.value.query) });

  if (!createTrainingStore.isEdit) {
    createTrainingStore.showModal = false;
    createTrainingStore.showExit = true;
  } else {
    createTrainingStore.showModal = false;
    // An update review closes here without the exit dialog: its markers must not follow the next training.
    prefillStore.reset();
  }
  if (hostsForm) paginate.fetchData();

  emit("reset");
}

onMounted(() => {
  // Docebo's rules depend on the company's LMS; the modal also opens from a Copilot import.
  void useCompaniesStore().fetchLmsProviders();

  if (!training.value.general.type) {
    type.value = null;
  } else if (training.value.general.type == TrainingTypeEnum.EXTERNAL) {
    type.value = TrainingTypeEnum.EXTERNAL;
  } else if (training.value.general.type == TrainingTypeEnum.INTERNAL) {
    type.value = TrainingTypeEnum.INTERNAL;
  }
});

// The AI pre-fill only describes the unsaved draft: once the training exists, the markers go.
watch(
  () => createTrainingStore.isEdit,
  (isEdit) => {
    if (isEdit) prefillStore.reset();
  },
);

watch(
  () => training.value.general.type,
  (value) => {
    if (!value) {
      type.value = null;
    } else if (value == TrainingTypeEnum.EXTERNAL) {
      type.value = TrainingTypeEnum.EXTERNAL;
    } else if (value == TrainingTypeEnum.INTERNAL) {
      type.value = TrainingTypeEnum.INTERNAL;
    }
  },
);
</script>
<template>
  <Transition name="slide-fade">
    <CenterModal
      v-if="!type"
      style="max-width: 720px; max-height: 688px; width: 100%; height: 100%"
      @hide="
        createTrainingStore.showModal = false;
        createTrainingStore.showExit = true;
      "
    >
      <template #body>
        <div class="project__modal body_regular" :class="'px-[64px]'">
          <TrainingChoiceTypeModal></TrainingChoiceTypeModal>
        </div>
      </template>
    </CenterModal>

    <GlobalModal v-else style="width: 100%; height: 100%" @hide="onClose()">
      <template #body>
        <div class="training__modal body_regular px-0">
          <HeaderDetails class="py-4" back-icon="arrow_left" :close="false" back @back-click="onClose()">
            <template v-if="!createTrainingStore.isEdit" #page
              >Créer une formation {{ type === TrainingTypeEnum.EXTERNAL ? "externe" : "interne" }}</template
            >
            <template v-else #page
              >Modifier la formation {{ type === TrainingTypeEnum.EXTERNAL ? "externe" : "interne" }}</template
            >
          </HeaderDetails>
          <div class="flex flex-1 min-h-0">
            <template v-if="hasProgram">
              <!-- Hidden rather than unmounted: re-mounting re-downloads the PDF and resets its pager. -->
              <TrainingProgramViewer v-show="prefillStore.isViewerOpen" />

              <!-- The folded panel: its toolbar strip, kept just wide enough for the icon to stay put. -->
              <div
                v-if="!prefillStore.isViewerOpen"
                class="flex shrink-0 flex-col border-r border-stroke-color bg-canvas-grey-color"
              >
                <div class="flex h-10 shrink-0 items-center border-b border-stroke-color bg-light-color px-4">
                  <SvgIcon
                    name="document"
                    class="h-[16px] w-[16px] shrink-0 cursor-pointer text-secondary-color"
                    title="Afficher le programme"
                    @click="prefillStore.openViewer()"
                  />
                </div>
              </div>
            </template>
            <div class="flex flex-1 min-w-0 flex-col">
              <div class="mt-6 w-[752px] max-w-full mx-auto px-4">
                <TrainingNav
                  v-model:currNav="createTrainingStore.nav"
                  :headers-nav="trainings.getNav(createTrainingStore.isEdit)"
                  :manual-nav="createTrainingStore.isEdit"
                >
                </TrainingNav>
              </div>
              <div class="overflow-auto h-full">
                <div class="w-[752px] max-w-full my-8 mx-auto">
                  <component :is="tab[createTrainingStore.nav]"></component>
                </div>
              </div>
              <TrainingFooter
                v-if="!readonly"
                v-model:currNav="createTrainingStore.nav"
                class="footer-core-training max-w-full"
              >
              </TrainingFooter>
            </div>
          </div>
        </div>
      </template>
    </GlobalModal>
  </Transition>
</template>

<style scoped lang="scss">
.project__modal {
  @apply grid pt-[48px] pb-[16px];
  display: flex;
  height: 100%;
  flex-direction: column;
}

.tooltip__container {
  @apply rounded-lg py-[32px];
  background-color: var(--black-dark);
  color: var(--white-main);
}

.training__modal {
  @apply grid;
  display: flex;
  height: 100%;
  flex-direction: column;
}

.footer-core-training {
  @apply mx-auto w-[752px];
  border-top: 1px solid var(--grey-lightest);
}

.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(50px);
  opacity: 0;
}
</style>
